import {
  Assignment,
  AutoAwesome,
  CleaningServices,
  Dashboard,
  GroupAdd,
  NotificationsNone,
  Payments,
  PendingActions,
  Person,
  RequestQuote,
  Security,
  Settings,
  ShoppingCart,
  TextFields,
  ViewKanban,
  Web,
} from '@mui/icons-material';

// Agrupamento do menu administrativo.
export const getAdminNavGroups = () => [
  {
    label: 'Principal',
    items: [
      { text: 'Dashboard', short: 'Painel', icon: Dashboard, path: '/dashboard' },
      // Pedidos antes de Clientes: é a tela que o admin abre o dia inteiro e
      // agora é a primeira parada depois do Painel (também no swipe mobile).
      { text: 'Pedidos', short: 'Pedidos', icon: ShoppingCart, path: '/cadastro-pedidos' },
      { text: 'Clientes', short: 'Clientes', icon: Person, path: '/cadastro-clientes' },
      { text: 'Pagamentos', short: 'Pagos', icon: Payments, path: '/payments' },
      {
        text: 'Notificações',
        short: 'Alertas',
        icon: NotificationsNone,
        path: '/notificacoes',
      },
      { text: 'Serviços', short: 'Serviços', icon: Assignment, path: '/cadastro-servicos' },
    ],
  },
  {
    label: 'Site & conteúdo',
    items: [
      { text: 'Orçamento', short: 'Orçamento', icon: RequestQuote, path: '/gerenciar-orcamento' },
      { text: 'Landing Page', short: 'Landing', icon: Web, path: '/gerenciar-landing-page' },
      { text: 'Quadros', short: 'Quadros', icon: ViewKanban, path: '/boards' },
      { text: 'Conversor de Texto', short: 'Conversor', icon: TextFields, path: '/text-converter' },
      { text: 'Instagram IA', short: 'Instagram IA', icon: AutoAwesome, path: '/instagram-ai' },
    ],
  },
  {
    label: 'Gestão',
    items: [
      {
        text: 'Cadastros Pendentes',
        short: 'Pendentes',
        icon: PendingActions,
        path: '/cadastros-pendentes',
      },
      { text: 'Indicações', short: 'Indicações', icon: GroupAdd, path: '/indicacoes' },
      { text: 'Segurança', short: 'Segurança', icon: Security, path: '/seguranca' },
      {
        text: 'Manutenção Storage',
        short: 'Storage',
        icon: CleaningServices,
        path: '/manutencao-storage',
      },
      { text: 'Configurações', short: 'Config', icon: Settings, path: '/configuracoes' },
    ],
  },
];

export const isAdminNavActive = (pathname, path) => {
  if (path === '/cadastro-pedidos') {
    return ['/cadastro-pedidos', '/visualizar-pedido', '/editar-pedido'].some((prefix) =>
      pathname.startsWith(prefix)
    );
  }
  if (path === '/cadastro-clientes') {
    return ['/cadastro-clientes', '/editar-cliente', '/dados-cliente'].some((prefix) =>
      pathname.startsWith(prefix)
    );
  }
  if (path === '/boards') return pathname.startsWith('/boards');
  if (path === '/cadastros-pendentes') return pathname.startsWith('/cadastros-pendentes');
  return pathname === path;
};

/**
 * Todos os itens do menu na ordem de leitura (usado pela faixa do topo no
 * mobile e pelo swipe entre páginas).
 */
export const getAdminNavItems = () => getAdminNavGroups().flatMap((group) => group.items);

/** Sentidos aceitos pelo swipe: arrastar para a esquerda avança a faixa. */
export const SENTIDOS_SWIPE = Object.freeze({ proximo: 1, anterior: -1 });

/**
 * Rota vizinha na faixa do topo — o alvo de um swipe horizontal.
 *
 * Sem wrap de propósito: nas pontas (`/dashboard` indo para trás e a última aba
 * indo para frente) o gesto não deve jogar o usuário para o outro extremo.
 * Também devolve `null` quando a rota atual não pertence a nenhuma aba (ex.:
 * `/`, `/text-converter`), para o gesto não adivinhar destino.
 *
 * Rotas de detalhe funcionam porque `isAdminNavActive` já as mapeia para a aba
 * dona (`/visualizar-pedido/:id` → Pedidos).
 *
 * @param {string} pathname Rota atual (`useLocation().pathname`).
 * @param {'proximo'|'anterior'} direcao Sentido do swipe.
 * @returns {string|null} Rota de destino, ou `null` quando não há para onde ir.
 */
export const getAdjacentAdminPath = (pathname, direcao) => {
  const passo = SENTIDOS_SWIPE[direcao];
  if (!pathname || !passo) return null;

  const itens = getAdminNavItems();
  const indice = itens.findIndex((item) => isAdminNavActive(pathname, item.path));
  if (indice === -1) return null;

  const destino = itens[indice + passo];
  return destino ? destino.path : null;
};

/**
 * Rotas que só podem ser renderizadas para admin.
 *
 * Centralizado aqui (fonte única) e consumido por ProtectedRoute, evitando a
 * lista de prefixos duplicada que deixava telas admin acessíveis a clientes.
 * `visualizar-pedido`, `boards` e `text-converter` ficam de fora porque também
 * são usados pelo portal do cliente (o backend valida o dono do pedido).
 */
export const ADMIN_ONLY_ROUTE_PREFIXES = [
  '/dashboard',
  '/notificacoes',
  '/cadastro-clientes',
  '/editar-cliente',
  '/dados-cliente',
  '/cadastro-pedidos',
  '/editar-pedido',
  '/cadastro-servicos',
  '/cadastros-pendentes',
  '/gerenciar-orcamento',
  '/gerenciar-landing-page',
  '/payments',
  '/instagram-ai',
  '/indicacoes',
  '/manutencao-storage',
  '/seguranca',
  '/configuracoes',
];

export const isAdminOnlyPath = (pathname = '') =>
  ADMIN_ONLY_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
